/**
 * src/lib/nhanXetDeck.js: đọc và ghi yêu cầu nhận xét pitch deck từ trình duyệt.
 *
 * Phía founder: tải PDF lên Storage TRƯỚC, rồi mới tạo document. Cloud Function
 * kích hoạt khi document được tạo, nên tệp phải có sẵn lúc đó.
 * Phía quản trị: đọc danh sách, bản nháp AI, ghi kết quả, yêu cầu AI chạy lại.
 * Quyền đọc ghi do Firestore Rules và Storage Rules quyết định, không phải ở đây.
 */
import { layDb, layStorage } from './firebase.js';
import { chuanHoaYeuCau, kiemTraYeuCau, chuanHoaNhanXet, banChoFounder, chuanHoaHinh } from '@shared/nhanXetDeck.js';

const ngay = (t) => (t?.toDate ? t.toDate() : null);

function tuDoc(d) {
  const x = d.data();
  return { id: d.id, ...x, createdAt: ngay(x.createdAt), guiLuc: ngay(x.guiLuc), capNhat: ngay(x.capNhat) };
}

async function fs() {
  const db = await layDb();
  if (!db) throw new Error('Website chưa kết nối được máy chủ.');
  return { db, f: await import('firebase/firestore/lite') };
}

/** Gửi deck. onTienDo nhận số 0 đến 100 trong lúc tải tệp. */
export async function guiYeuCau(user, raw, tep, onTienDo = () => {}) {
  const y = chuanHoaYeuCau(raw);
  const loi = kiemTraYeuCau(y, tep);
  if (loi.length) throw new Error(loi.join(' '));
  if (!user?.uid || !user.email) throw new Error('Cần đăng nhập để gửi deck.');
  const { db, f } = await fs();
  const ref = f.doc(f.collection(db, 'deck_reviews'));
  const deckPath = `deck-reviews/${user.uid}/${ref.id}/deck.pdf`;

  const storage = await layStorage();
  const { ref: sRef, uploadBytesResumable } = await import('firebase/storage');
  await new Promise((xong, hong) => {
    const tai = uploadBytesResumable(sRef(storage, deckPath), tep, { contentType: 'application/pdf' });
    tai.on('state_changed', (s) => onTienDo(Math.round((s.bytesTransferred / s.totalBytes) * 100)), hong, xong);
  }).catch((e) => {
    throw new Error(e?.code === 'storage/unauthorized'
      ? 'Máy chủ từ chối tệp. Kiểm tra lại tệp là PDF và nhỏ hơn 20 MB.'
      : `Chưa tải được tệp: ${e?.message || e}`);
  });

  await f.setDoc(ref, {
    ...y, uid: user.uid, email: user.email,
    deckPath, deckTen: String(tep.name || 'deck.pdf').slice(0, 200),
    deckMB: Math.round((tep.size / 1024 / 1024) * 10) / 10,
    status: 'moi', createdAt: f.serverTimestamp(),
  });
  return ref.id;
}

export async function docYeuCauCuaToi(uid) {
  const { db, f } = await fs();
  const snap = await f.getDocs(f.query(f.collection(db, 'deck_reviews'), f.where('uid', '==', uid)));
  return snap.docs.map(tuDoc).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
}

export async function docYeuCau(id) {
  const { db, f } = await fs();
  const s = await f.getDoc(f.doc(db, 'deck_reviews', id));
  return s.exists() ? tuDoc(s) : null;
}

/** Xoá yêu cầu và tệp. Cloud Function cũng dọn tệp, ở đây xoá trước cho chắc. */
export async function xoaYeuCau(yc) {
  if (yc.deckPath) {
    try {
      const storage = await layStorage();
      const { ref, deleteObject } = await import('firebase/storage');
      await deleteObject(ref(storage, yc.deckPath));
    } catch { /* tệp có thể đã được dọn */ }
  }
  const { db, f } = await fs();
  await f.deleteDoc(f.doc(db, 'deck_reviews', yc.id));
}

// ── Quản trị ───────────────────────────────────────────────────────────

export async function docTatCaYeuCau(limit = 100) {
  const { db, f } = await fs();
  const snap = await f.getDocs(f.query(f.collection(db, 'deck_reviews'), f.orderBy('createdAt', 'desc'), f.limit(limit)));
  return snap.docs.map(tuDoc);
}

export async function docBanNhapAI(id) {
  const { db, f } = await fs();
  const s = await f.getDoc(f.doc(db, 'deck_reviews', id, 'noi_bo', 'ai'));
  if (!s.exists()) return null;
  const x = s.data();
  return { ...x, batDauLuc: ngay(x.batDauLuc), xongLuc: ngay(x.xongLuc), yeuCauLuc: ngay(x.yeuCauLuc) };
}

/** Yêu cầu AI chạy (lại). Cloud Function bắt thay đổi này. */
export async function yeuCauChayAI(id, chiDanThem = '') {
  const { db, f } = await fs();
  // Nếu đang kẹt ở 'requested', ghi một trạng thái trung gian trước: trigger chỉ
  // chạy khi có thay đổi, ghi lại đúng giá trị cũ thì không có gì kích hoạt.
  await f.setDoc(f.doc(db, 'deck_reviews', id, 'noi_bo', 'ai'), { status: 'cho' }, { merge: true });
  await f.setDoc(f.doc(db, 'deck_reviews', id, 'noi_bo', 'ai'),
    { status: 'requested', chiDanThem: String(chiDanThem).slice(0, 2000), yeuCauLuc: f.serverTimestamp() }, { merge: true });
}

/** Lưu bản đang biên tập (chưa gửi founder). */
export async function luuBanBienTap(id, nx) {
  const { db, f } = await fs();
  await f.setDoc(f.doc(db, 'deck_reviews', id, 'noi_bo', 'ai'), { banBienTap: chuanHoaNhanXet(nx), capNhatBienTap: f.serverTimestamp() }, { merge: true });
}

export async function datTrangThai(id, status, them = {}) {
  const { db, f } = await fs();
  await f.updateDoc(f.doc(db, 'deck_reviews', id), { status, capNhat: f.serverTimestamp(), ...them });
}

/**
 * Gửi kết quả cho founder. Cloud Function gửi email khi status đổi sang da_gui.
 * Kèm hình slide (ảnh và vị trí chữ) nếu đã dựng xong, vì founder không đọc
 * được noi_bo/hinh.
 */
export async function guiKetQua(id, nx, hinh = null) {
  const { db, f } = await fs();
  await luuBanBienTap(id, nx);
  const trang = hinh?.status === 'xong' ? chuanHoaHinh(hinh.trang) : [];
  await f.updateDoc(f.doc(db, 'deck_reviews', id), {
    ketQua: banChoFounder(nx), status: 'da_gui', tuDong: false, guiLuc: f.serverTimestamp(), capNhat: f.serverTimestamp(),
    ...(trang.length ? { hinhSlide: trang } : {}),
  });
}

/** Ảnh từng slide và vị trí chữ (Cloud Function dungHinh tạo). Chỉ quản trị. */
export async function docHinh(id) {
  const { db, f } = await fs();
  const s = await f.getDoc(f.doc(db, 'deck_reviews', id, 'noi_bo', 'hinh'));
  if (!s.exists()) return null;
  const x = s.data();
  return { ...x, yeuCauLuc: ngay(x.yeuCauLuc), xongLuc: ngay(x.xongLuc) };
}

/** Yêu cầu dựng (lại) hình slide, ví dụ cho deck gửi trước khi có tính năng này. */
export async function yeuCauDungHinh(id) {
  const { db, f } = await fs();
  const ref = f.doc(db, 'deck_reviews', id, 'noi_bo', 'hinh');
  await f.setDoc(ref, { status: 'cho' }, { merge: true });
  await f.setDoc(ref, { status: 'requested', yeuCauLuc: f.serverTimestamp() }, { merge: true });
}

const boNhoAnh = new Map();
/** Đường dẫn xem ảnh slide. Lỗi (ảnh đã xoá, không có quyền) thì trả về ''. */
export function linkAnh(path) {
  if (!path) return Promise.resolve('');
  // Đường dẫn web sẵn (bản xem thử, ảnh tĩnh) thì dùng luôn.
  if (/^(https?:|data:|\/)/.test(path)) return Promise.resolve(path);
  if (!boNhoAnh.has(path)) {
    boNhoAnh.set(path, (async () => {
      const storage = await layStorage();
      const { ref, getDownloadURL } = await import('firebase/storage');
      return getDownloadURL(ref(storage, path));
    })().catch(() => ''));
  }
  return boNhoAnh.get(path);
}

export async function linkXemDeck(path) {
  const storage = await layStorage();
  const { ref, getDownloadURL } = await import('firebase/storage');
  return getDownloadURL(ref(storage, path));
}

export async function docGocNhin() {
  const { db, f } = await fs();
  const s = await f.getDoc(f.doc(db, 'cau_hinh', 'nhan_xet_deck'));
  return s.exists() ? s.data().gocNhin || '' : '';
}

export async function luuGocNhin(gocNhin) {
  const { db, f } = await fs();
  await f.setDoc(f.doc(db, 'cau_hinh', 'nhan_xet_deck'), { gocNhin: String(gocNhin).slice(0, 20000), capNhat: f.serverTimestamp() }, { merge: true });
}
